import React, { useState, useEffect, useRef, useCallback } from 'react';
// MapLibre GL (WebGL). Rollback: volver a './components/MapComponent'
import MapComponent from './components/MapComponentGL';
import Sidebar from './components/Sidebar';
import CrmDirectoryPanel from './components/CrmDirectoryPanel';
import Login from './components/Login';
import { LanguageProvider } from './contexts/LanguageContext';
import { useAuth } from './contexts/AuthContext';
import { api, fetchWithAuth } from './config/api';
import { dataCache } from './utils/cache';
import './App.css';

function AppContent() {
  const [mapData, setMapData] = useState([]);
  const [statistics, setStatistics] = useState({
    total_zipcodes: 0,
    total_population: 0,
    total_hispanic: 0,
    avg_hispanic_percentage: 0
  });
  const [zipcodeFilters, setZipcodeFilters] = useState({
    country: '',
    dma: '',
    state: '',
    region: '',
    certainteedBranch: '',
    qxoBranch: '',
    city: [],
    dba: ''
  });
  const [loading, setLoading] = useState(false);
  const [tradeAreaLoading, setTradeAreaLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('initializing');
  const [serverStatus, setServerStatus] = useState('checking');
  const [dmas, setDmas] = useState([]);
  const [estados, setEstados] = useState([]);
  const [regions, setRegions] = useState([]);
  const [certainteedStates, setCertainteedStates] = useState([]);
  const [certainteedDbas, setCertainteedDbas] = useState([]);
  const [certainteedBranches, setCertainteedBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [qxoBranches, setQxoBranches] = useState([]);
  const [qxoCities, setQxoCities] = useState([]);
  const [qxoDbas, setQxoDbas] = useState([]);
  const [apolloDbas, setApolloDbas] = useState([]);
  const [apolloCities, setApolloCities] = useState([]);
  const [certainteedCities, setCertainteedCities] = useState([]);
  
  // Estado para controlar la visibilidad de las capas
  const [layerVisibility, setLayerVisibility] = useState({
    zipcodes: false,     // Capa de códigos postales (desactivada por defecto)
    densityZip: false,    // Capa de densidad de códigos postales
    certainteed: false,  // Capa de sucursales SRS (desactivada por defecto)
    certainteedTradeArea: false, // Capa de área de comercio SRS (desactivada por defecto)
    qxo: false,  // Capa de sucursales QXO (desactivada por defecto)
    qxoTradeArea: false, // Capa de área de comercio QXO (desactivada por defecto)
    apollo: true, // Capa de puntos Apollo (CertainTeed Current Certified Contractors - activa por defecto)
    notCertifiedContractors: false // Capa de Not Certified Contractors (desactivada por defecto)
  });
  
  const [bounds, setBounds] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [hispanicConcentration, setHispanicConcentration] = useState(''); // Nuevo estado
  const [isTopHispanicActive, setIsTopHispanicActive] = useState(false); // Estado para ranking de hispanos
  const [topHispanicTradeAreas, setTopHispanicTradeAreas] = useState([]); // Estado para trade areas del ranking
  
  // Estado para filtros de tipos de contratistas (colores)
  const [contractorFilters, setContractorFilters] = useState({
    'azul-oscuro': true,
    'verde': true,
    'naranja-oscuro': true,
    'gris': true,
    'rosa-fucsia': true,
    'rojo': true
  });
  
  // Referencias para throttling
  const loadTimeoutRef = useRef(null);
  const lastLoadRef = useRef(Date.now());
  const lastApiCallRef = useRef({});
  
  // Control de peticiones para evitar condiciones de carrera
  const [mainTab, setMainTab] = useState('map');

  const [requestVersion, setRequestVersion] = useState(0);
  const abortControllerRef = useRef(null);
  const lastRequestVersionRef = useRef(0);
  
  // Control de peticiones activas
  const activeRequestsRef = useRef(new Map());
  const requestCounterRef = useRef(0);
  
  // Refs para evitar llamadas duplicadas
  const loadingAdvancedStatsRef = useRef(false);
  const loadingInitialDataRef = useRef(false);

  // Mapeo oficial de abreviaturas de estados a nombres completos
  const stateAbbreviations = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  // Mapeo inverso: nombres completos a abreviaturas para sucursales
  const stateToAbbreviation = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
    'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
    'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
    'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
    'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
    'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
    'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
    'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
  };

  // Función para cancelar peticiones activas
  const cancelActiveRequests = (type = 'all') => {
    
    if (type === 'all') {
      // Cancelar todas las peticiones activas
      activeRequestsRef.current.forEach((controller, key) => {
        controller.abort();
      });
      activeRequestsRef.current.clear();
    } else {
      // Cancelar peticiones específicas por tipo
      const keysToCancel = Array.from(activeRequestsRef.current.keys())
        .filter(key => key.includes(type));
      
      keysToCancel.forEach(key => {
        const controller = activeRequestsRef.current.get(key);
        controller.abort();
        activeRequestsRef.current.delete(key);
      });
    }
  };

  // Función para crear una petición con AbortController
  const createAbortableRequest = (url, options = {}) => {
    const requestId = `${Date.now()}-${++requestCounterRef.current}`;
    const controller = new AbortController();
    
    // Agregar a peticiones activas
    activeRequestsRef.current.set(requestId, controller);
    
    
    const fetchPromise = fetchWithAuth(url, {
      ...options,
      signal: controller.signal
    }).finally(() => {
      // Remover de peticiones activas cuando termine
      activeRequestsRef.current.delete(requestId);
    });
    
    return { promise: fetchPromise, controller, requestId };
  };

  // Cargar datos iniciales
  useEffect(() => {
    // Evitar cargar datos múltiples veces en StrictMode
    let isMounted = true;
    
    const loadData = async () => {
      if (!isMounted) return;
      
      await loadInitialData();
    };
    
    loadData();
    
    return () => {
      isMounted = false;
      // Cancelar todas las peticiones activas al desmontar
      cancelActiveRequests('all');
    };
  }, []);

  // Detectar cambios en el filtro de estado para centrar el mapa y filtrar sucursales
  useEffect(() => {
    if (zipcodeFilters.state) {
      loadFilteredBranches(zipcodeFilters.state);
    } else {
      setFilteredBranches(certainteedBranches);
    }
  }, [zipcodeFilters.state, certainteedBranches]);

  // Recargar ciudades cuando cambie el filtro de estado
  useEffect(() => {
    const loadFilteredCities = async () => {
      try {
        let certainteedCitiesUrl = api.certainteedCities();
        let qxoCitiesUrl = api.qxoCities();
        let apolloCitiesUrl = api.apolloCities();
        
        if (zipcodeFilters.state) {
          const stateAbbreviation = stateToAbbreviation[zipcodeFilters.state] || zipcodeFilters.state;
          certainteedCitiesUrl += `?state=${encodeURIComponent(stateAbbreviation)}`;
          qxoCitiesUrl += `?state=${encodeURIComponent(stateAbbreviation)}`;
          apolloCitiesUrl += `?state=${encodeURIComponent(stateAbbreviation)}`;
        }
        
        const [certainteedCitiesRes, qxoCitiesRes, apolloCitiesRes] = await Promise.all([
          fetchWithAuth(certainteedCitiesUrl),
          fetchWithAuth(qxoCitiesUrl),
          fetchWithAuth(apolloCitiesUrl)
        ]);
        
        let certainteedCitiesData = [];
        let qxoCitiesData = [];
        let apolloCitiesData = [];
        
        if (certainteedCitiesRes.ok) {
          certainteedCitiesData = await certainteedCitiesRes.json();
          setCertainteedCities(certainteedCitiesData);
        } else {
          setCertainteedCities([]);
        }
        
        if (qxoCitiesRes.ok) {
          qxoCitiesData = await qxoCitiesRes.json();
          setQxoCities(qxoCitiesData);
        } else {
          setQxoCities([]);
        }
        
        if (apolloCitiesRes.ok) {
          const contentType = apolloCitiesRes.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            apolloCitiesData = await apolloCitiesRes.json();
          } else {
            await apolloCitiesRes.text();
            apolloCitiesData = [];
          }
        } else {
          await apolloCitiesRes.text();
          apolloCitiesData = [];
        }
        setApolloCities(apolloCitiesData);
        
        // Conservar solo las ciudades seleccionadas que sigan existiendo en la lista filtrada
        if (zipcodeFilters.city.length > 0) {
          const allCities = new Set([...certainteedCitiesData, ...qxoCitiesData, ...apolloCitiesData]);
          const stillValid = zipcodeFilters.city.filter(c => allCities.has(c));
          if (stillValid.length !== zipcodeFilters.city.length) {
            handleZipcodeFiltersChange('city', stillValid);
          }
        }
      } catch (error) {
        setCertainteedCities([]);
        setQxoCities([]);
        setApolloCities([]);
        // Si hay error, limpiar las ciudades seleccionadas
        if (zipcodeFilters.city.length > 0) {
          handleZipcodeFiltersChange('city', []);
        }
      }
    };
    
    loadFilteredCities();
  }, [zipcodeFilters.state]);

  // Filtrar sucursales QXO según el estado seleccionado
  useEffect(() => {
    const loadFilteredQxoBranches = async () => {
      try {
        if (zipcodeFilters.state) {
          const stateAbbreviation = stateToAbbreviation[zipcodeFilters.state] || zipcodeFilters.state;
          const response = await fetchWithAuth(`${api.qxoBranches()}?state=${encodeURIComponent(stateAbbreviation)}`);
          const branches = await response.json();
          // setFilteredQxoBranches(branches); // Si necesitamos un estado separado
        } else {
          // Si no hay estado seleccionado, usar todas las sucursales QXO
          // setFilteredQxoBranches(qxoBranches); // Si necesitamos un estado separado
        }
      } catch (error) {
        // setFilteredQxoBranches([]); // Si necesitamos un estado separado
      }
    };
    
    loadFilteredQxoBranches();
  }, [zipcodeFilters.state, qxoBranches]);

  // Recargar Apollo DBAs cuando cambien los filtros de estado o ciudad
  useEffect(() => {
    const loadFilteredApolloDbas = async () => {
      try {
        let url = api.apolloDbas();
        const params = new URLSearchParams();
        
        if (zipcodeFilters.state) {
          params.append('state', zipcodeFilters.state);
        }
        
        if (zipcodeFilters.city.length > 0) {
          params.append('city', zipcodeFilters.city.join(','));
        }
        
        if (params.toString()) {
          url += '?' + params.toString();
        }
        
        const response = await fetchWithAuth(url);
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const dbas = await response.json();
            setApolloDbas(dbas);
            
            // Si hay un DBA seleccionado y ya no está en la lista filtrada, limpiarlo
            if (zipcodeFilters.dba && !dbas.includes(zipcodeFilters.dba)) {
              handleZipcodeFiltersChange('dba', '');
            }
          } else {
            setApolloDbas([]);
            // Si hay un DBA seleccionado, limpiarlo
            if (zipcodeFilters.dba) {
              handleZipcodeFiltersChange('dba', '');
            }
          }
        } else {
          setApolloDbas([]);
          // Si hay un DBA seleccionado, limpiarlo
          if (zipcodeFilters.dba) {
            handleZipcodeFiltersChange('dba', '');
          }
        }
      } catch (error) {
        setApolloDbas([]);
        // Si hay un DBA seleccionado, limpiarlo
        if (zipcodeFilters.dba) {
          handleZipcodeFiltersChange('dba', '');
        }
      }
    };
    
    loadFilteredApolloDbas();
  }, [zipcodeFilters.state, zipcodeFilters.city.join(',')]);

  // Efecto unificado para manejar todos los cambios que requieren recarga de datos
  useEffect(() => {
    // Verificar si realmente necesitamos recargar datos
    const hasSignificantChanges = 
      zipcodeFilters.country !== '' ||
      zipcodeFilters.dma !== '' ||
      zipcodeFilters.region !== '' ||
      zipcodeFilters.hispanic_concentration !== '' ||
      zipcodeFilters.certainteedBranch !== '' ||
      zipcodeFilters.state !== '';
    
    if (!hasSignificantChanges) {
      return;
    }
    
    // Debounce muy agresivo para evitar múltiples llamadas rápidas
    const timeoutId = setTimeout(() => {
      setLoading(true);
      
      const loadData = async () => {
        try {
          // Solo cargar estadísticas, no datos del mapa
          await loadStatistics();
          
          // También cargar estadísticas avanzadas automáticamente
          await loadAdvancedStatistics(true, zipcodeFilters);
          
          setLastUpdate(Date.now());
        } catch (error) {
        } finally {
          setLoading(false);
        }
      };
      
      loadData();
    }, 1500); // Aumentado a 1500ms para mejor debounce
    
    return () => clearTimeout(timeoutId);
  }, [
    zipcodeFilters.country, 
    zipcodeFilters.dma, 
    zipcodeFilters.region, 
    zipcodeFilters.hispanic_concentration, 
    zipcodeFilters.certainteedBranch,
    zipcodeFilters.state
  ]);

  // Verificar si el servidor está disponible
  const checkServerAvailability = async () => {
    try {
      const response = await fetchWithAuth(api.dmas(), { 
        method: 'HEAD',
        signal: AbortSignal.timeout(5000) // 5 segundos timeout
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // Función para limpiar cache desde la interfaz
  const clearAllCache = () => {
    try {
      // Limpiar localStorage
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith('geoCache_') || 
            key.startsWith('statistics_') || 
            key.startsWith('advanced_') ||
            key.includes('cache') ||
            key.includes('Cache')) {
          localStorage.removeItem(key);
        }
      }
      
      // Limpiar sessionStorage
      const sessionKeys = Object.keys(sessionStorage);
      for (const key of sessionKeys) {
        if (key.startsWith('geoCache_') || 
            key.startsWith('statistics_') || 
            key.startsWith('advanced_') ||
            key.includes('cache') ||
            key.includes('Cache')) {
          sessionStorage.removeItem(key);
        }
      }
      
      // Limpiar memoria
      dataCache.emergencyCleanup();
      
      alert('Cache cleared. The page will reload automatically.');
      
      // Recargar página después de 1 segundo
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      alert('Error clearing cache. Please reload the page manually.');
    }
  };

  const loadInitialData = async () => {
    // Guard para evitar llamadas duplicadas
    if (loadingInitialDataRef.current) {
      return;
    }
    
    // Evitar cargar datos si ya se han cargado
    if (dmas.length > 0 && estados.length > 0) {
      return;
    }
    
    loadingInitialDataRef.current = true;
    setLoading(true);
    try {
      
      // Verificar disponibilidad del servidor
      const serverAvailable = await checkServerAvailability();
      if (!serverAvailable) {
        setLoading(false);
        return;
      }
      
      // Limpieza inicial agresiva del cache
      try {
        dataCache.cleanup();
        // Limpieza adicional de emergencia si es necesario
        if (localStorage.getItem('cache_cleaned') !== 'true') {
          dataCache.emergencyCleanup();
          localStorage.setItem('cache_cleaned', 'true');
        }
      } catch (error) {
        dataCache.emergencyCleanup();
      }
      
      // Cargar catálogos de estados y sucursales
      const [dmasRes, estadosRes, regionsRes, certainteedStatesRes, certainteedDbasRes, certainteedBranchesRes, certainteedCitiesRes, qxoBranchesRes, qxoCitiesRes, qxoDbasRes, apolloDbasRes, apolloCitiesRes] = await Promise.all([
        fetchWithAuth(api.dmas()),
        fetchWithAuth(api.estados()),
        fetchWithAuth(api.regions()),
        fetchWithAuth(api.certainteedStates()),
        fetchWithAuth(api.certainteedDbas()),
        fetchWithAuth(api.certainteedBranches()),
        fetchWithAuth(api.certainteedCities()),
        fetchWithAuth(api.qxoBranches()),
        fetchWithAuth(api.qxoCities()),
        fetchWithAuth(api.qxoDbas()),
        fetchWithAuth(api.apolloDbas()),
        fetchWithAuth(api.apolloCities())
      ]);

      const dmasData = await dmasRes.json();
      const estadosData = await estadosRes.json();
      const regionsData = await regionsRes.json();
      const certainteedStatesData = await certainteedStatesRes.json();
      const certainteedDbasData = await certainteedDbasRes.json();
      const certainteedBranchesData = await certainteedBranchesRes.json();
      const certainteedCitiesData = await certainteedCitiesRes.json();
      const qxoBranchesData = await qxoBranchesRes.json();
      const qxoCitiesData = await qxoCitiesRes.json();
      const qxoDbasData = await qxoDbasRes.json();
      
      // Cargar Apollo DBAs con manejo de errores
      let apolloDbasData = [];
      try {
        if (apolloDbasRes.ok) {
          const contentType = apolloDbasRes.headers.get('content-type');
          
          if (contentType && contentType.includes('application/json')) {
            apolloDbasData = await apolloDbasRes.json();
          } else {
            await apolloDbasRes.text();
            // Si no es JSON, intentar parsear como array vacío o manejar el error
            apolloDbasData = [];
          }
        } else {
          await apolloDbasRes.text();
          apolloDbasData = [];
        }
      } catch (error) {
        apolloDbasData = [];
      }

      // Unificar y normalizar la lista de estados
      const allStatesSet = new Set([
        ...estadosData.map(e => e && e.trim()),
        ...certainteedStatesData.map(e => e && e.trim())
      ]);
      
      // Normalizar y crear objetos { value, label }
      const allStates = Array.from(allStatesSet)
        .filter(Boolean)
        .map(state => {
          const normalizedState = state.trim();
          // Si es una abreviatura, convertir a nombre completo
          const fullName = stateAbbreviations[normalizedState.toUpperCase()] || normalizedState;
          return {
            value: normalizedState, // valor real para consulta (mantiene el original de la DB)
            label: fullName // nombre completo para visualización
          };
        })
        // Eliminar duplicados basándose en el label (nombre completo)
        .filter((state, index, self) => 
          index === self.findIndex(s => s.label === state.label)
        )
        .sort((a, b) => a.label.localeCompare(b.label));

      setDmas(dmasData);
      setEstados(allStates);
      setRegions(regionsData);
      setCertainteedStates(certainteedStatesData);
      setCertainteedDbas(certainteedDbasData);
      setCertainteedBranches(certainteedBranchesData);
      setCertainteedCities(certainteedCitiesData);
      setQxoBranches(qxoBranchesData);
      setQxoCities(qxoCitiesData);
      setQxoDbas(qxoDbasData);
      setApolloDbas(apolloDbasData);
      
      // Cargar Apollo Cities con manejo de errores
      let apolloCitiesData = [];
      try {
        if (apolloCitiesRes.ok) {
          const contentType = apolloCitiesRes.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            apolloCitiesData = await apolloCitiesRes.json();
          } else {
            await apolloCitiesRes.text();
            apolloCitiesData = [];
          }
        } else {
          await apolloCitiesRes.text();
          apolloCitiesData = [];
        }
      } catch (error) {
        apolloCitiesData = [];
      }
      setApolloCities(apolloCitiesData);
      
      // Cargar estadísticas iniciales
      await loadStatistics();
      
      // Cargar estadísticas avanzadas
      await loadAdvancedStatistics();
      
      // Mostrar estadísticas del cache
      const cacheStats = dataCache.getStats();
      
    } catch (error) {
    } finally {
      setLoading(false);
      loadingInitialDataRef.current = false;
    }
  };

  // Función optimizada para calcular límite basado en zoom
  const getOptimalLimit = (zoom) => {
    // Límites aumentados para mostrar más polígonos simultáneamente
    if (zoom >= 12) return 5000;   // Zoom muy alto: máximo de polígonos
    if (zoom >= 10) return 3000;   // Zoom alto: cantidad muy alta
    if (zoom >= 8) return 2000;    // Zoom medio: cantidad alta
    if (zoom >= 6) return 1500;    // Zoom bajo: cantidad aumentada
    return 1000;                   // Zoom muy bajo: cantidad base aumentada
  };

  // Función de throttling para evitar llamadas múltiples a la API
  // Función para crear una nueva petición con control de versión
  const createSafeRequest = async (requestFn, version) => {
    // Cancelar petición anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Crear nuevo AbortController
    abortControllerRef.current = new AbortController();
    
    try {
      const result = await requestFn(abortControllerRef.current.signal);
      
      // Verificar que esta es la petición más reciente
      if (version === lastRequestVersionRef.current) {
        return result;
      } else {
        return null;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    }
  };

  const throttledApiCall = (key, callback, delay = 5000) => {
    const now = Date.now();
    const lastCall = lastApiCallRef.current[key] || 0;
    
    // Verificar si ya hay una petición en curso para esta clave
    if (lastApiCallRef.current[`${key}_in_progress`]) {
      return;
    }
    
    // Throttling más agresivo
    if (now - lastCall < delay) {
      return;
    }
    
    // Verificar si los datos ya están en cache
    const cacheKey = key.replace('statistics_', '');
    if (cacheKey.includes('"certainteedBranch":"')) {
      // Para branches específicas, no usar cache
    } else {
      // Verificar cache antes de hacer petición
      const cachedData = localStorage.getItem(`statistics_${cacheKey}`);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (parsed.data && !parsed.data.error) {
            return;
          }
        } catch (error) {
        }
      }
    }
    
    lastApiCallRef.current[key] = now;
    lastApiCallRef.current[`${key}_in_progress`] = true;
    
    // Incrementar versión de petición
    const newVersion = requestVersion + 1;
    setRequestVersion(newVersion);
    lastRequestVersionRef.current = newVersion;
    
    
    // Ejecutar callback con control de versión
    createSafeRequest(async (signal) => {
      try {
        await callback(signal);
      } finally {
        // Marcar como completada
        lastApiCallRef.current[`${key}_in_progress`] = false;
      }
    }, newVersion);
  };

  // Función para calcular estadísticas del trade area
  const calculateTradeAreaStatistics = (tradeAreas) => {
    
    if (!tradeAreas || tradeAreas.length === 0) {
      return {
        total_zipcodes: 0,
        total_population: 0,
        total_hispanic: 0,
        avg_hispanic_percentage: 0
      };
    }

    let totalZipCodes = 0;
    let totalPopulation = 0;
    let totalHispanicPopulation = 0;
    let validPercentages = [];
    let uniqueZipCodes = new Set(); // Para contar zip codes únicos

    tradeAreas.forEach((area, index) => {
      // Procesar área
      if (area.core_zip_code) {
        uniqueZipCodes.add(area.core_zip_code);
      }
      
      // Contar zip codes únicos
      if (area.core_zip_code) {
        uniqueZipCodes.add(area.core_zip_code);
      }

      // Sumar población total
      if (area.population_zipcode && !isNaN(area.population_zipcode)) {
        const pop = parseInt(area.population_zipcode);
        totalPopulation += pop;
      } else {
      }

      // Sumar población hispana
      if (area.population_hispanic_zipcode && !isNaN(area.population_hispanic_zipcode)) {
        const hispPop = parseInt(area.population_hispanic_zipcode);
        totalHispanicPopulation += hispPop;
      } else {
      }

      // Recolectar porcentajes válidos para el promedio
      if (area.percent_hispanics_trade_area && !isNaN(area.percent_hispanics_trade_area)) {
        const percentage = parseFloat(area.percent_hispanics_trade_area);
        validPercentages.push(percentage);
      } else {
      }
    });

    // Usar el número de zip codes únicos
    totalZipCodes = uniqueZipCodes.size;

    // Calcular porcentaje promedio de hispanos
    const averageHispanicPercentage = validPercentages.length > 0 
      ? validPercentages.reduce((sum, val) => sum + val, 0) / validPercentages.length 
      : 0;

    const result = {
      total_zipcodes: totalZipCodes,
      total_population: totalPopulation,
      total_hispanic: totalHispanicPopulation,
      avg_hispanic_percentage: Math.round(averageHispanicPercentage * 100) / 100
    };

    return result;
    return result;
  };

  const loadMapData = async (bounds = null, zoom = 4) => {
    // Si estamos en modo ranking, no cargar datos adicionales
    if (isTopHispanicActive) {
      return;
    }
    
    // Verificar si las capas de zipcodes están activas
    const zipcodesLayerActive = layerVisibility.zipcodes || layerVisibility.densityZip;
    
    if (!zipcodesLayerActive) {
      setMapData([]);
      return;
    }
    
    // Cancelar peticiones anteriores de datos del mapa
    cancelActiveRequests('map-data');
    
    try {
      // Intentar cargar desde cache primero
      const cachedData = await dataCache.getZipcodes(bounds, zoom, zipcodeFilters);
      if (cachedData) {
        setMapData(cachedData);
        return;
      }

      const limit = getOptimalLimit(zoom);
      let url = `${api.zipcodes()}?limit=${limit}&mode=polygons`; // Usar polígonos por defecto
      
      // Solo aplicar bounds si están disponibles y el zoom es suficiente
      if (bounds && zoom >= 6) {
        url += `&bounds=${JSON.stringify(bounds)}`;
      }
      
      if (zipcodeFilters.country) url += `&country=${zipcodeFilters.country}`;
      if (zipcodeFilters.dma) url += `&dma=${zipcodeFilters.dma}`;
      if (zipcodeFilters.state) url += `&estado=${zipcodeFilters.state}`;
      if (zipcodeFilters.region) url += `&region=${zipcodeFilters.region}`;

      // Crear petición con AbortController
      const { promise: responsePromise } = createAbortableRequest(
        url,
        { 
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const response = await responsePromise;
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      let processedData = [];
      
      // Procesar respuesta basada en el formato (array legacy o FeatureCollection)
      if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
        processedData = data.features;
      } else if (Array.isArray(data)) {
        // Formato legacy - convertir a FeatureCollection
        processedData = data.map(item => ({
          type: 'Feature',
          geometry: typeof item.geometry === 'string' ? JSON.parse(item.geometry) : item.geometry,
          properties: { ...item, geometry: undefined }
        }));
      } else {
        processedData = [];
      }
      
      setMapData(processedData);
      
      // Guardar en cache
      await dataCache.setZipcodes(bounds, zoom, zipcodeFilters, processedData);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      setMapData([]); // Establecer array vacío en caso de error
    }
  };

  const loadStatistics = async () => {
    try {
      // Cancelar peticiones anteriores de estadísticas
      cancelActiveRequests('statistics');
      
      // Verificar cache primero
      const cachedStats = await dataCache.getStatistics(zipcodeFilters);
      
      if (cachedStats && !cachedStats.error) {
        setStatistics(cachedStats);
        return;
      }
      
      
      
      let url = api.statistics();
      const params = new URLSearchParams();
      
      if (zipcodeFilters.dma) params.append('dma', zipcodeFilters.dma);
      if (zipcodeFilters.state) params.append('estado', zipcodeFilters.state);
      if (zipcodeFilters.region) params.append('region', zipcodeFilters.region);
      if (zipcodeFilters.certainteedBranch) {
        params.append('branch', zipcodeFilters.certainteedBranch);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      
      // Crear petición con AbortController
      const { promise: responsePromise } = createAbortableRequest(
        url,
        { 
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const response = await responsePromise;
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(`API error: ${data.error}`);
      }
      
      setStatistics(data);
      
      // Guardar en cache
      await dataCache.setStatistics(zipcodeFilters, data);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      setStatistics({
        total_zipcodes: 0,
        total_population: 0,
        total_hispanic: 0,
        avg_hispanic_percentage: 0
      });
    }
  };

  const loadAdvancedStatistics = async (ignoreCache = false, filtersToUse = null) => {
    // Guard para evitar llamadas duplicadas
    if (loadingAdvancedStatsRef.current) {
      return;
    }
    
    loadingAdvancedStatsRef.current = true;
    try {
      const currentFilters = filtersToUse || zipcodeFilters;
      cancelActiveRequests('statistics-advanced');
      let url = api.statisticsAdvanced();
      const params = new URLSearchParams();
      if (currentFilters.state) {
        params.append('estado', currentFilters.state);
      }
      if (currentFilters.certainteedBranch) {
        params.append('branch', currentFilters.certainteedBranch);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      const { promise: responsePromise } = createAbortableRequest(
        url,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      const response = await responsePromise;
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const advancedData = await response.json();
      setStatistics(prevStats => {
        const combined = {
          ...prevStats,
          ...advancedData
        };
        return combined;
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
    } finally {
      loadingAdvancedStatsRef.current = false;
    }
  };

  const handleZipcodeFiltersChange = (newFilters) => {
    setZipcodeFilters(newFilters);
    
    // Auto-aplicar filtros cuando cambien
    handleApplyZipcodeFilters(newFilters);
  };

  const handleApplyZipcodeFilters = async (filtersToApply = null) => {
    const currentFilters = filtersToApply || zipcodeFilters;
    
    // Cancelar todas las peticiones activas antes de aplicar nuevos filtros
    cancelActiveRequests('all');
    
    setLoading(true);
    try {
      // Cargar estadísticas básicas con nuevos filtros
      await loadStatistics();
      
      // SIEMPRE cargar estadísticas avanzadas con filtros actuales
      await loadAdvancedStatistics(true, currentFilters); // Pasar filtros actuales
      
      setLastUpdate(Date.now());
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onLayerVisibilityChange = (layer, value) => {
    if (layer === 'certainteedTradeArea' && value) {
      // Activar loading cuando se activa la capa de trade areas
      setTradeAreaLoading(true);
      
      setLayerVisibility(prev => ({
        ...prev,
        densityZip: false,
        certainteedTradeArea: true
      }));
    } else if (layer === 'densityZip' && value) {
      setLayerVisibility(prev => ({
        ...prev,
        densityZip: true,
        certainteedTradeArea: false
      }));
    } else {
      setLayerVisibility(prev => ({ ...prev, [layer]: value }));
    }
  };

  const handleMapBoundsChange = (bounds, zoom) => {
    setBounds(bounds);
    setCurrentZoom(zoom);
    
    // Si estamos en modo ranking, no cargar datos adicionales
    if (isTopHispanicActive) {
      return;
    }
    
    // Verificar si las capas de zipcodes están activas antes de cargar datos
    const zipcodesLayerActive = layerVisibility.zipcodes || layerVisibility.densityZip;
    
    if (!zipcodesLayerActive) {
      return;
    }
    
    // Throttle para evitar demasiadas llamadas
    const now = Date.now();
    
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
    }
    
    // Solo cargar si ha pasado suficiente tiempo o si es un cambio significativo de zoom
    const timeSinceLastLoad = now - lastLoadRef.current;
    const shouldLoadImmediately = timeSinceLastLoad > 2000 || Math.abs(zoom - currentZoom) > 1;
    
    if (shouldLoadImmediately) {
      loadMapData(bounds, zoom);
      lastLoadRef.current = now;
    } else {
      // Throttle para cambios menores
      loadTimeoutRef.current = setTimeout(() => {
        loadMapData(bounds, zoom);
        lastLoadRef.current = Date.now();
      }, 1000);
    }
  };

  // Función para cargar sucursales filtradas por estado
  const loadFilteredBranches = async (state) => {
    try {
      if (state) {
        // Convertir nombre completo a abreviatura para la API
        const stateAbbreviation = stateToAbbreviation[state] || state;
        const response = await fetchWithAuth(`${api.certainteedBranches()}?state=${encodeURIComponent(stateAbbreviation)}`);
        const branches = await response.json();
        setFilteredBranches(branches);
      } else {
        // Si no hay estado seleccionado, usar todas las sucursales
        setFilteredBranches(certainteedBranches);
      }
    } catch (error) {
      setFilteredBranches([]);
    }
  };

  // Función para mostrar las 200 áreas con más hispanos
  const handleShowTopHispanic = async () => {
    try {
      setIsTopHispanicActive(true);
      setLoading(true);

      // Limpiar todas las capas anteriores antes de mostrar el ranking
      setLayerVisibility({
        zipcodes: false,
        densityZip: false,
        certainteed: false,
        certainteedTradeArea: false
      });

      // Cargar datos del ranking (solo sucursales CertainTeed)
      const response = await fetchWithAuth(api.topHispanicAreas());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Actualizar datos del mapa con las 200 sucursales
      setMapData(data.features || []);
      setTopHispanicTradeAreas(data.trade_areas || []);

      // Cargar estadísticas del ranking
      const statsResponse = await fetchWithAuth(api.statisticsTopHispanic());
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStatistics(statsData);
      }

      // Activar solo las capas necesarias para el ranking
      setLayerVisibility({
        zipcodes: false,
        densityZip: true, // Para mostrar las sucursales CertainTeed
        certainteed: false,
        certainteedTradeArea: true
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setIsTopHispanicActive(false);
    }
  };

  // Función para limpiar el ranking de hispanos
  const handleClearTopHispanic = async () => {
    try {
      setIsTopHispanicActive(false);
      setLoading(true);
      
      // Restaurar las capas a su estado normal
      setLayerVisibility({
        zipcodes: false,
        densityZip: false,
        certainteed: true,
        certainteedTradeArea: true
      });
      
      // Limpiar datos del mapa
      setMapData([]);
      setTopHispanicTradeAreas([]);
      
      // Recargar estadísticas normales
      await loadStatistics();
      await loadAdvancedStatistics(true);
      
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <LanguageProvider>
      <div className="app">
        <div className="app-header">
          <div className="app-header__row">
            <div className="app-title">
              PULSE™ – People Understanding through Location, Space & Engagement.
            </div>
            <nav className="app-main-tabs" aria-label="Main views">
              <button
                type="button"
                className={mainTab === 'map' ? 'is-active' : ''}
                onClick={() => setMainTab('map')}
              >
                Map
              </button>
              <button
                type="button"
                className={mainTab === 'crm' ? 'is-active' : ''}
                onClick={() => setMainTab('crm')}
              >
                Certified
              </button>
              <button
                type="button"
                className={mainTab === 'drm' ? 'is-active' : ''}
                onClick={() => setMainTab('drm')}
              >
                Not certified
              </button>
            </nav>
          </div>
        </div>
        <div className="app-content">
          {mainTab === 'map' && (
          <Sidebar
            zipcodeFilters={zipcodeFilters}
            onZipcodeFiltersChange={handleZipcodeFiltersChange}
            onApplyZipcodeFilters={handleApplyZipcodeFilters}
            statistics={statistics}
            dmas={dmas}
            estados={estados}
            regions={regions}
            certainteedStates={certainteedStates}
            certainteedDbas={certainteedDbas}
            apolloDbas={apolloDbas}
            certainteedBranches={certainteedBranches}
            filteredBranches={filteredBranches}
            qxoBranches={qxoBranches}
            qxoCities={qxoCities}
            certainteedCities={certainteedCities}
            apolloCities={apolloCities}
            layerVisibility={layerVisibility}
            onLayerVisibilityChange={onLayerVisibilityChange}
            hispanicConcentration={hispanicConcentration}
            setHispanicConcentration={setHispanicConcentration}
            clearCache={clearAllCache}
            tradeAreaLoading={tradeAreaLoading}
            onShowTopHispanic={handleShowTopHispanic}
            onClearTopHispanic={handleClearTopHispanic}
            isTopHispanicActive={isTopHispanicActive}
            contractorFilters={contractorFilters}
            setContractorFilters={setContractorFilters}
          />
          )}
          {mainTab === 'map' && (
          <div className="map-section">
            <MapComponent
              data={mapData}
              filters={zipcodeFilters}
              onBoundsChange={handleMapBoundsChange}
              loading={loading}
              layerVisibility={layerVisibility}
              hispanicConcentration={hispanicConcentration}
              tradeAreaLoading={tradeAreaLoading}
              onTradeAreaLoadingChange={setTradeAreaLoading}
              isTopHispanicActive={isTopHispanicActive}
              topHispanicTradeAreas={topHispanicTradeAreas}
              apolloDbas={apolloDbas}
              contractorFilters={contractorFilters}
            />
          </div>
          )}
          {mainTab === 'crm' && (
            <div className="map-section crm-directory-section">
              <CrmDirectoryPanel mode="crm" />
            </div>
          )}
          {mainTab === 'drm' && (
            <div className="map-section crm-directory-section">
              <CrmDirectoryPanel mode="drm" />
            </div>
          )}
        </div>
      </div>
    </LanguageProvider>
  );
}

function App() {
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Mostrar loading mientras se verifica autenticación
  if (authLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Mostrar login si no está autenticado
  if (!isAuthenticated) {
    return (
      <LanguageProvider>
        <Login />
      </LanguageProvider>
    );
  }

  // Mostrar aplicación principal si está autenticado
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App; 